import {
  Injectable,
  EnvironmentInjector,
  inject,
  runInInjectionContext,
} from "@angular/core";
import {
  Storage,
  ref,
  uploadString,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "@angular/fire/storage";

export class UploadError extends Error {
  constructor(public readonly key: string) {
    super(key);
  }
}

const IMAGE_INPUT_MAX_BYTES = 25 * 1024 * 1024;
const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
const DOCUMENT_EXTENSIONS = new Set(["pdf", "xlsx", "xls", "csv", "docx", "doc", "pptx", "ppt", "txt"]);

/** Safe object-name fragment from a user filename. */
export function safeFileName(name: string): string {
  return String(name || "file")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

async function decodeImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file);
  } catch {
    // Fallback for browsers without createImageBitmap support for the type.
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => { URL.revokeObjectURL(url); reject(new UploadError("WEBSITE_IMAGE_UNSUPPORTED")); };
      img.src = url;
    });
  }
}

/** Decode `file` and draw it on a canvas scaled to `maxEdge` px on the long
 *  side (never upscaled). `scaled` is false when it already fit. */
async function drawScaled(file: File, maxEdge: number): Promise<{ canvas: HTMLCanvasElement; scaled: boolean }> {
  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await decodeImage(file);
  } catch {
    throw new UploadError("WEBSITE_IMAGE_UNSUPPORTED");
  }
  const w = (source as any).naturalWidth || source.width;
  const h = (source as any).naturalHeight || source.height;
  if (!w || !h) throw new UploadError("WEBSITE_IMAGE_UNSUPPORTED");
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new UploadError("WEBSITE_IMAGE_UNSUPPORTED");
  ctx.drawImage(source as any, 0, 0, outW, outH);
  return { canvas, scaled: scale < 1 };
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

// Raster types re-encoded in their own format; anything else (SVG, GIF which
// may be animated, AVIF, BMP, HEIC) is stored as uploaded.
const KEEP_FORMAT_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

/** Logo/background: scaled down in its own format, never converted to JPEG.
 *  Falls back to the original file whenever re-encoding wouldn't help. */
async function scaleKeepingFormat(file: File, maxEdge: number): Promise<Blob> {
  if (!KEEP_FORMAT_TYPES.has(file.type)) return file;
  let drawn: { canvas: HTMLCanvasElement; scaled: boolean };
  try {
    drawn = await drawScaled(file, maxEdge);
  } catch {
    return file;
  }
  if (!drawn.scaled) return file;
  const blob = await canvasBlob(drawn.canvas, file.type, file.type === "image/png" ? undefined : 0.92);
  // A browser that can't encode the type (WebP on older Safari) returns PNG.
  if (!blob || blob.type !== file.type || blob.size >= file.size) return file;
  return blob;
}

// Source types that can carry transparency.
const ALPHA_TYPES = new Set(["image/png", "image/webp", "image/gif", "image/avif"]);

/** True when any pixel is not fully opaque. */
function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
  return false;
}

/** Photo encoding: JPEG, or PNG when the photo really has transparency. */
async function encodePhoto(canvas: HTMLCanvasElement, alpha: boolean, quality: number): Promise<Blob> {
  const blob = await canvasBlob(canvas, alpha ? "image/png" : "image/jpeg", alpha ? undefined : quality);
  if (!blob) throw new UploadError("WEBSITE_IMAGE_UNSUPPORTED");
  return blob;
}

/** `canvas` scaled down to `maxEdge` px on the long side. */
function shrinkCanvas(canvas: HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(canvas.width, canvas.height));
  if (scale === 1) return canvas;
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(canvas.width * scale));
  out.height = Math.max(1, Math.round(canvas.height * scale));
  out.getContext("2d")?.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

/**
 * Thin wrapper over modular Firebase Storage — the replacement for the global
 * compat `firebase.storage()` accessor. Centralizes the injection-context
 * wrapping (AngularFire's modular storage functions are zone-wrapped and call
 * inject(NgZone), so every call must run inside runInInjectionContext, same as
 * the Database/Auth migrations) so call sites stay one-liners and don't each
 * have to deal with the storage `ref` name colliding with the database `ref`.
 */
@Injectable({ providedIn: "root" })
export class StorageService {
  private readonly storage = inject(Storage);
  private readonly injector = inject(EnvironmentInjector);

  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  /** Upload a base64 `data_url` string; resolves to the download URL. */
  async uploadDataUrl(path: string, dataUrl: string): Promise<string> {
    const r = this.run(() => ref(this.storage, path));
    await this.run(() => uploadString(r, dataUrl, "data_url"));
    return this.run(() => getDownloadURL(r));
  }

  /** Upload a File/Blob; resolves to the download URL. */
  async uploadFile(path: string, file: Blob): Promise<string> {
    const r = this.run(() => ref(this.storage, path));
    await this.run(() => uploadBytes(r, file));
    return this.run(() => getDownloadURL(r));
  }

  /**
   * Upload a photo for screen display: decoded, scaled to `maxEdge` px on the
   * long side and saved as JPEG 0.82 (PNG only when the photo has
   * transparency). A 12 MB phone photo lands as a few hundred KB. Logos go
   * through uploadBrandImage instead (format kept). Throws
   * UploadError("WEBSITE_IMAGE_UNSUPPORTED") when the browser can't decode
   * the file (HEIC on Chrome) and ("WEBSITE_UPLOAD_TOO_LARGE") over the input
   * cap. Photos visitors can download pass `extension: true`.
   */
  async uploadImage(
    path: string,
    file: File,
    opts: { maxEdge?: number; quality?: number; extension?: boolean } = {}
  ): Promise<string> {
    return (await this.uploadPhotoVariants(path, file, opts)).url;
  }

  /**
   * uploadImage plus a `thumbEdge` px copy ({path}-thumb) for photo grids;
   * the full photo is for the lightbox. Resolves to both download URLs.
   */
  async uploadImageWithThumb(
    path: string,
    file: File,
    opts: { maxEdge?: number; quality?: number; extension?: boolean; thumbEdge?: number } = {}
  ): Promise<{ url: string; thumbUrl: string }> {
    const r = await this.uploadPhotoVariants(path, file, { ...opts, thumbEdge: opts.thumbEdge ?? 640 });
    return { url: r.url, thumbUrl: r.thumbUrl as string };
  }

  private async uploadPhotoVariants(
    path: string,
    file: File,
    opts: { maxEdge?: number; quality?: number; extension?: boolean; thumbEdge?: number }
  ): Promise<{ url: string; thumbUrl: string | null }> {
    if (file.size > IMAGE_INPUT_MAX_BYTES) throw new UploadError("WEBSITE_UPLOAD_TOO_LARGE");
    const quality = opts.quality ?? 0.82;
    const { canvas } = await drawScaled(file, opts.maxEdge ?? 1600);
    const alpha = ALPHA_TYPES.has(file.type) && hasTransparency(canvas);
    const blob = await encodePhoto(canvas, alpha, quality);
    // extension: name the object .jpg/.png after the re-encoded type, so a
    // visitor who downloads the photo gets a file their viewer opens.
    const ext = opts.extension ? (alpha ? ".png" : ".jpg") : "";
    const url = await this.uploadFile(`${path}${ext}`, blob);
    if (!opts.thumbEdge) return { url, thumbUrl: null };
    const thumb = await encodePhoto(shrinkCanvas(canvas, opts.thumbEdge), alpha, quality);
    const thumbUrl = await this.uploadFile(`${path}-thumb${ext}`, thumb);
    return { url, thumbUrl };
  }

  /**
   * Upload a club logo or clock background: scaled to `maxEdge` px on the long
   * side in its original format (PNG stays PNG, JPEG re-encoded at 0.92), so
   * brand images never pick up JPEG artifacts. Uploaded unchanged when it
   * already fits or the browser can't re-encode it.
   */
  async uploadBrandImage(path: string, file: File, maxEdge: number): Promise<string> {
    if (file.size > IMAGE_INPUT_MAX_BYTES) throw new UploadError("WEBSITE_UPLOAD_TOO_LARGE");
    return this.uploadFile(path, await scaleKeepingFormat(file, maxEdge));
  }

  /**
   * Upload a document (PDF, spreadsheet, Word, slides, CSV, text) after an
   * extension/MIME allowlist and a size cap. Returns URL + metadata for the
   * files section.
   */
  async uploadDocument(
    path: string,
    file: File
  ): Promise<{ url: string; name: string; size: number; mime: string }> {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!DOCUMENT_EXTENSIONS.has(ext)) throw new UploadError("WEBSITE_FILE_TYPE_NOT_ALLOWED");
    if (file.size > DOCUMENT_MAX_BYTES) throw new UploadError("WEBSITE_UPLOAD_TOO_LARGE");
    const url = await this.uploadFile(path, file);
    return { url, name: file.name, size: file.size, mime: file.type || "" };
  }

  /** Delete the object at `path`. */
  async delete(path: string): Promise<void> {
    const r = this.run(() => ref(this.storage, path));
    await this.run(() => deleteObject(r));
  }
}
